import React, { useState, useEffect } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { PHONE, PHONE_DISPLAY } from '../../data/appData';

const NAV_LINKS = [
  { label: 'Ana Sayfa', href: '#anasayfa' },
  { label: 'Hizmetler', href: '#hizmetler' },
  { label: 'Teknolojiler', href: '#teknolojiler' },
  { label: 'Galeri', href: '#galeri' },
  { label: 'İletişim', href: '#iletisim' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('anasayfa');
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const ids = NAV_LINKS.map(l => l.href.replace('#', ''));
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { threshold: 0.25 }
    );
    ids.forEach(id => { const el = document.getElementById(id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      const start = window.scrollY;
      const target = el.getBoundingClientRect().top + window.scrollY - 72;
      const distance = target - start;
      const duration = Math.min(900, Math.max(400, Math.abs(distance) * 0.4));
      let startTime = null;
      const ease = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const step = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        window.scrollTo(0, start + distance * ease(progress));
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
    setMenuOpen(false);
  };

  const isActive = (href) => activeSection === href.replace('#', '');

  return (
    <header style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)',
      background: theme === 'dark'
        ? 'rgba(2,6,23,0.85)'
        : (scrolled ? 'rgba(244,246,249,0.75)' : 'rgba(255,255,255,0.55)'),
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderBottom: theme === 'dark'
        ? '1px solid rgba(0,180,204,0.12)'
        : '1px solid rgba(0,0,0,0.06)',
      boxShadow: theme === 'dark'
        ? '0 1px 0 rgba(0,180,204,0.08)'
        : '0 2px 12px rgba(0,0,0,0.06)',
    }}>
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '72px' }}>

        <a href="#anasayfa" onClick={e => handleNavClick(e, '#anasayfa')} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', animation: 'fadeInDown 0.6s both' }}>
          <img
            src="/logo.png"
            alt="Taşgın Tesisat"
            style={{
              height: '56px',
              width: 'auto',
              objectFit: 'contain',
              display: 'block',
              filter: theme === 'dark'
                ? 'brightness(1.1) drop-shadow(0 0 6px rgba(0,212,240,0.35))'
                : 'brightness(1.0)',
              transition: 'filter 0.3s ease',
            }}
          />
        </a>

        <nav style={{ display: 'flex', alignItems: 'center', gap: '4px' }} className="desktop-nav">
          {NAV_LINKS.map(link => (
            <a key={link.href} href={link.href} onClick={e => handleNavClick(e, link.href)} style={{
              padding: '8px 16px', borderRadius: 'var(--radius-full)',
              fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '0.9rem', letterSpacing: '0.01em',
              color: isActive(link.href) ? 'var(--teal)' : (theme === 'dark' ? 'rgba(255,255,255,0.85)' : 'var(--text-secondary)'),
              background: isActive(link.href) ? 'rgba(0,180,204,0.1)' : 'transparent',
              border: isActive(link.href) ? '1px solid rgba(0,180,204,0.3)' : '1px solid transparent',
              transition: 'all 0.2s', textDecoration: 'none',
            }}>{link.label}</a>
          ))}
        </nav>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={toggleTheme} style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--surface-1)', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', cursor: 'pointer', transition: 'all 0.2s' }} title="Tema Değiştir">
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>

          <a href={`tel:${PHONE}`} className="phone-btn" style={{ padding: '8px 16px', borderRadius: 'var(--radius-full)', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.85rem', color: '#25D366', border: '1.5px solid rgba(37,211,102,0.4)', background: 'rgba(37,211,102,0.08)', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}>
            📞 {PHONE_DISPLAY}
          </a>

          <a href="#iletisim" onClick={e => handleNavClick(e, '#iletisim')} className="btn btn-accent" style={{ fontSize: '0.85rem', padding: '10px 20px' }}>
            🚨 Randevu Al
          </a>

          <button className="mobile-menu-btn" onClick={() => setMenuOpen(m => !m)} style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--surface-1)', border: '1px solid var(--border-color)', display: 'none', alignItems: 'center', justifyContent: 'center', fontSize: '20px', cursor: 'pointer' }}>
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div style={{ background: 'var(--bg-card)', borderTop: '1px solid var(--border-color)', padding: '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {NAV_LINKS.map(link => (
            <a key={link.href} href={link.href} onClick={e => handleNavClick(e, link.href)} style={{ padding: '14px 16px', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '1rem', color: isActive(link.href) ? 'var(--teal)' : 'var(--text-primary)', background: isActive(link.href) ? 'rgba(0,180,204,0.08)' : 'transparent', borderLeft: isActive(link.href) ? '3px solid var(--teal)' : '3px solid transparent', textDecoration: 'none' }}>
              {link.label}
            </a>
          ))}
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <a href={`tel:${PHONE}`} className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center', fontSize: '0.9rem', textDecoration: 'none' }}>📞 {PHONE_DISPLAY}</a>
            <a href="#iletisim" onClick={e => handleNavClick(e, '#iletisim')} className="btn btn-accent" style={{ flex: 1, justifyContent: 'center', fontSize: '0.9rem' }}>🚨 Randevu Al</a>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 1024px) { .desktop-nav { display: none !important; } .phone-btn { display: none !important; } .mobile-menu-btn { display: flex !important; } }
        @media (max-width: 640px) { .btn-accent { display: none !important; } }
      `}</style>
    </header>
  );
}
